"use client";

/**
 * @file        src/components/MessengerPicker.tsx
 * @description Optional «Где удобнее переписываться?» block of the lead form:
 *              a caption and four round messenger icons (WhatsApp / Telegram /
 *              Max / WeChat) on one row and, once one is chosen, a free field
 *              for the visitor's account in it. Rendered by LeadForm in every variant, right under the
 *              phone. Owner request 2026-10-14: a phone call often goes
 *              unanswered and the right messenger is hard to guess.
 * @universal   MessengerPicker — the ONE messenger-choice control of the site.
 *              Registered in knowledge/universals/components.md.
 *              Accepts: value, onChange, onInteract, inputClassName.
 * @rule        OPTIONAL, NOTHING PRE-SELECTED. The phone stays the only
 *              required field of the form (invariant B-I2). A second tap on
 *              the chosen icon clears it.
 * @rule        ICONS, NOT TEXT CHIPS (owner decision 2026-10-14) — they fit
 *              on one row with the caption. The name is still there for
 *              everyone who cannot see the glyph: aria-label + title, and the
 *              field placeholder names the messenger once one is chosen.
 * @rule        OUTLINED UNTIL CHOSEN. Tail blocks show ContactChannels — OUR
 *              messengers as filled round buttons — right under the form. A
 *              filled icon here before the visitor taps it would look like a
 *              link to us. Glyphs and brand fills come from messengerChannels
 *              (WeChat, absent there, is defined below).
 * @rule        `type="button"` IS LOAD-BEARING: these live inside a <form>.
 * @rule        Tap target ≥ 44 px (h-11 w-11) — invariant B-I9.
 * @rule        NO «Позвонить» CHIP (owner decision 2026-10-14). The phone is
 *              already collected; this block exists only to say where to
 *              WRITE. It replaced variant Б's old «Как связаться» row.
 * @rule        Changing the messenger clears the account field: a Telegram
 *              @username left under «WhatsApp» would send the manager to the
 *              wrong place.
 */

import type { ReactNode } from "react";
import { MESSENGER_CHANNELS } from "@/lib/messengerChannels";
import { WeChatGlyph } from "@/components/icons/messengerGlyphs";

/** Offered messengers, rendered in this order. */
export const MESSENGER_OPTIONS = ["WhatsApp", "Telegram", "Max", "WeChat"] as const;

/** Client-side cap on the account field; usernames and IDs are short. */
const LOGIN_MAX = 64;

type Messenger = (typeof MESSENGER_OPTIONS)[number];

/** Placeholder of the account field — it also names the chosen messenger. */
const LOGIN_PLACEHOLDER: Record<Messenger, string> = {
  WhatsApp: "Ваш WhatsApp, если номер другой",
  Telegram: "Ваш Telegram: @username или номер",
  Max: "Ваш Max: номер или ник",
  WeChat: "Ваш WeChat ID",
};

/** Glyph colour while not chosen — the brand colour on a white button. */
const GLYPH_COLOR: Record<Messenger, string> = {
  WhatsApp: "text-[#25D366]",
  Telegram: "text-[#2AABEE]",
  Max: "text-[#6B5BF0]",
  WeChat: "text-[#07C160]",
};

function iconFor(name: Messenger): { glyph: ReactNode; bg: string } {
  const channel = MESSENGER_CHANNELS.find((ch) => ch.name === name);
  if (channel) return { glyph: channel.glyph, bg: channel.bg };
  return { glyph: <WeChatGlyph />, bg: "bg-[#07C160]" };
}

export interface MessengerValue {
  /** Chosen messenger, or null when the visitor has not chosen. */
  messenger: string | null;
  /** The visitor's account in that messenger; may stay empty. */
  login: string;
}

interface MessengerPickerProps {
  value: MessengerValue;
  onChange: (next: MessengerValue) => void;
  /** Fired on any touch — LeadForm uses it for its form_start goal. */
  onInteract?: () => void;
  /** The host form's input classes, so the field matches its siblings. */
  inputClassName: string;
}

/**
 * Messenger chips plus the conditional account field.
 *
 * @input  value / onChange — controlled state owned by the form
 * @output the block; the account field renders only after a choice
 * @important Holds no state of its own — LeadForm passes the value to
 *            composeLeadMessage, which turns it into the manager's line.
 */
export default function MessengerPicker({
  value,
  onChange,
  onInteract,
  inputClassName,
}: MessengerPickerProps) {
  const chosen = value.messenger as Messenger | null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <p id="lead-messenger-label" className="flex-1 basis-44 text-left text-sm font-medium text-text">
          Где удобнее переписываться?{" "}
          <span className="font-normal text-text-muted">(необязательно)</span>
        </p>
        <div role="group" aria-labelledby="lead-messenger-label" className="flex gap-2">
          {MESSENGER_OPTIONS.map((name) => {
            const selected = chosen === name;
            const { glyph, bg } = iconFor(name);
            return (
              <button
                key={name}
                type="button"
                aria-pressed={selected}
                aria-label={name}
                title={name}
                onClick={() => {
                  onInteract?.();
                  onChange({ messenger: selected ? null : name, login: "" });
                }}
                className={`grid h-11 w-11 cursor-pointer place-items-center rounded-full border transition-colors ${
                  selected
                    ? `${bg} border-transparent text-white ring-2 ring-primary ring-offset-2`
                    : `border-border bg-white ${GLYPH_COLOR[name]} hover:border-current`
                }`}
              >
                {glyph}
              </button>
            );
          })}
        </div>
      </div>
      {chosen && (
        <input
          type="text"
          aria-label={`Аккаунт в ${chosen}`}
          value={value.login}
          maxLength={LOGIN_MAX}
          placeholder={LOGIN_PLACEHOLDER[chosen]}
          className={inputClassName}
          onFocus={onInteract}
          onChange={(e) => {
            onInteract?.();
            onChange({ messenger: chosen, login: e.target.value });
          }}
        />
      )}
    </div>
  );
}
